import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  HitterDataSource,
  HitterRosterStatus,
} from 'lib_ts/enums/hitters.enums';
import { useEffect } from 'react';
import useHittersStore from 'stores/use-hitters-store';

// reapply filters to the hitters in store, unset active hitter if filtered away
const useSyncHittersFilters = () => {
  const filters = useHittersStore((s) => s.filters);
  const hitters = useHittersStore((s) => s.hitters);
  const filtered = useHittersStore((s) => s.filtered);

  // MARK: when filters change, reapply them to the hitters in store
  useEffect(() => {
    const safeNameFilter = filters.name?.toLowerCase() ?? '';

    const nextFiltered = hitters
      .filter((h) => {
        if (!safeNameFilter) {
          return true;
        }

        return h.name.toLowerCase().includes(safeNameFilter);
      })
      .filter((h) => !filters.side || filters.side === h.side)
      .filter((h) => !filters.level || filters.level === h.level)
      .filter((h) => {
        if (!filters.status) {
          return true;
        }

        switch (filters.status) {
          case HitterRosterStatus.NA: {
            // no status => old hitter, treat the same as manual
            return !h.status || h.status === HitterRosterStatus.NA;
          }

          case HitterRosterStatus.OffRoster:
          case HitterRosterStatus.OnRoster:
          default: {
            return filters.status === h.status;
          }
        }
      })
      .filter((h) => {
        if (!filters.source) {
          return true;
        }

        switch (filters.source) {
          case HitterDataSource.Manual: {
            // no source => old hitter, treat the same as manual
            return !h.source || h.source === HitterDataSource.Manual;
          }

          case HitterDataSource.StatsApi:
          case HitterDataSource.Csv:
          default: {
            return filters.source === h.source;
          }
        }
      });

    // sort results by name
    nextFiltered.sort((a, b) => StringHelper.safeSort(a.name, b.name));

    useHittersStore.setState({ filtered: nextFiltered });
  }, [
    filters.name,
    filters.side,
    filters.level,
    filters.status,
    filters.source,
    hitters,
  ]);

  // MARK: when filtered changes, if active hitter is no longer visible, unset it
  useEffect(() => {
    const latestActiveHitter = useHittersStore.getState().activeHitter;

    if (!latestActiveHitter) {
      // do nothing
      return;
    }

    if (filtered.find((h) => h._id === latestActiveHitter._id)) {
      // still visible, do nothing
      return;
    }

    // unset active hitter
    useHittersStore.setState({ activeHitter: undefined });
  }, [filtered]);
};

export default useSyncHittersFilters;
